import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Shield, CheckCircle, ArrowLeft, LogOut, Image as ImageIcon, X, ClipboardList, AlertTriangle, Package, UserPlus, Users as UsersIcon, Phone, Upload, FolderOpen, MapPin, Calendar, Download, ChevronLeft, ChevronRight, Settings, QrCode } from 'lucide-react';
import QRScanner from '../components/QRScanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extract a warranty number from a scanned text. Accepts both bare numbers
// (e.g. "GDC-2026-0001") and URLs containing a ?nr=... query parameter.
function extractWarrantyNumber(text) {
  if (!text) return '';
  const trimmed = String(text).trim();
  // Try URL parsing first
  try {
    const u = new URL(trimmed);
    const nr = u.searchParams.get('nr');
    if (nr) return nr.trim().toUpperCase();
  } catch { /* not a URL */ }
  // Look for the GDC pattern anywhere in the text
  const m = trimmed.match(/GDC-[A-Z0-9]+-\d+/i);
  if (m) return m[0].toUpperCase();
  return trimmed.toUpperCase();
}

function DealerPortal() {
  const navigate = useNavigate();
  const [step, setStep] = useState('login');
  const [dealerInfo, setDealerInfo] = useState(null);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [pinCode, setPinCode] = useState('');
  const [message, setMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const response = await fetch(`${API}/dealer/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setDealerInfo(data);
        if (data.requires_pin === false || data.is_demo_account) {
          // Demo accounts skip PIN — go straight to dashboard.
          localStorage.setItem('dealer_info', JSON.stringify(data));
          localStorage.setItem('dealer_pin', 'demo-no-pin');
          setStep('dashboard');
        } else {
          setStep('pin');
        }
      } else {
        setMessage(data.detail || 'Feil brukernavn eller passord');
      }
    } catch (e) {
      setMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen.');
    }
  };

  const handlePinVerify = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const response = await fetch(`${API}/dealer/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealer_id: dealerInfo.dealer_id, pin_code: pinCode })
      });
      if (response.ok) {
        localStorage.setItem('dealer_info', JSON.stringify(dealerInfo));
        localStorage.setItem('dealer_pin', pinCode);
        setStep('dashboard');
      } else {
        const err = await response.json().catch(() => ({}));
        setMessage(err.detail || 'Feil PIN-kode');
        setPinCode('');
      }
    } catch {
      setMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen.');
    }
  };

  useEffect(() => {
    const storedDealer = localStorage.getItem('dealer_info');
    const storedPin = localStorage.getItem('dealer_pin');
    if (storedDealer && storedPin) {
      setDealerInfo(JSON.parse(storedDealer));
      setPinCode(storedPin);
      setStep('dashboard');
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('dealer_info');
    localStorage.removeItem('dealer_pin');
    setDealerInfo(null);
    setPinCode('');
    setCredentials({ username: '', password: '' });
    setStep('login');
  };

  if (step === 'login') return <LoginScreen onLogin={handleLogin} credentials={credentials} setCredentials={setCredentials} message={message} navigate={navigate} />;
  if (step === 'pin') return <PinScreen onVerify={handlePinVerify} pinCode={pinCode} setPinCode={setPinCode} message={message} dealerInfo={dealerInfo} />;
  if (step === 'dashboard') return <DashboardScreen dealerInfo={dealerInfo} onLogout={handleLogout} />;
  if (step === 'register') return <RegisterWarrantyScreen dealerInfo={dealerInfo} onBack={() => setStep('dashboard')} onSuccess={() => setStep('dashboard')} />;
  if (step === 'warranties') return <MyWarrantiesScreen dealerInfo={dealerInfo} onBack={() => setStep('dashboard')} />;
  if (step === 'settings') return <SettingsScreen dealerInfo={dealerInfo} onBack={() => setStep('dashboard')} />;
  if (step === 'datasheets') return <DatasheetsScreen onBack={() => setStep('dashboard')} />;
  return null;
}

// Login Screen
function LoginScreen({ onLogin, credentials, setCredentials, message, navigate }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-700 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <Shield className="mx-auto text-emerald-800" size={64} />
            <h1 className="text-3xl font-bold text-stone-900 mt-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Forhandler-portal
            </h1>
            <p className="text-stone-600 mt-2">Gravsteinoppfriskeren</p>
          </div>
          <form onSubmit={onLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Brukernavn</label>
              <input type="text" value={credentials.username} onChange={(e) => setCredentials({...credentials, username: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" required data-testid="dealer-username-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Passord</label>
              <input type="password" value={credentials.password} onChange={(e) => setCredentials({...credentials, password: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" required data-testid="dealer-password-input" />
            </div>
            {message && <p className="text-red-600 text-center text-sm" data-testid="login-error-message">{message}</p>}
            <button type="submit" className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-4 text-lg font-medium transition-colors" data-testid="dealer-login-btn">
              Logg inn
            </button>
          </form>
          <button onClick={() => navigate('/')} className="w-full mt-4 text-stone-600 hover:text-stone-900 flex items-center justify-center gap-2">
            <ArrowLeft size={20} /> Tilbake til forsiden
          </button>
        </div>
      </div>
    </div>
  );
}

// PIN Screen
function PinScreen({ onVerify, pinCode, setPinCode, message, dealerInfo }) {
  const handlePinInput = (digit) => { if (pinCode.length < 4) setPinCode(pinCode + digit); };
  const handlePinDelete = () => setPinCode(pinCode.slice(0, -1));

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-700 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Velkommen, {dealerInfo?.contact_person}!
            </h2>
            <p className="text-stone-600">{dealerInfo?.company_name}</p>
            <p className="text-sm text-stone-500 mt-4">Skriv inn din 4-sifrede PIN-kode</p>
          </div>
          <form onSubmit={onVerify}>
            <div className="flex justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center text-2xl font-bold ${pinCode.length > i ? 'bg-emerald-800 border-emerald-800 text-white' : 'bg-stone-50 border-stone-300 text-stone-300'}`}>
                  {pinCode.length > i ? '\u25CF' : ''}
                </div>
              ))}
            </div>
            {message && <p className="text-red-600 text-center text-sm mb-4">{message}</p>}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[1,2,3,4,5,6,7,8,9].map((num) => (
                <button key={num} type="button" onClick={() => handlePinInput(num.toString())} className="h-16 bg-stone-100 hover:bg-stone-200 rounded-lg text-2xl font-bold text-stone-900 transition-colors">{num}</button>
              ))}
              <div></div>
              <button type="button" onClick={() => handlePinInput('0')} className="h-16 bg-stone-100 hover:bg-stone-200 rounded-lg text-2xl font-bold text-stone-900 transition-colors">0</button>
              <button type="button" onClick={handlePinDelete} className="h-16 bg-red-100 hover:bg-red-200 rounded-lg text-stone-900 transition-colors flex items-center justify-center"><X size={32} /></button>
            </div>
            <button type="submit" disabled={pinCode.length !== 4} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-4 text-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              Bekreft
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Dashboard Screen with kit count
function DashboardScreen({ dealerInfo, onLogout }) {
  const navigate = useNavigate();
  const [view, setView] = useState('home');
  const [prefilledWarranty, setPrefilledWarranty] = useState('');

  const goRegister = (wn) => {
    setPrefilledWarranty(wn || '');
    setView('register');
  };
  const [kitInfo, setKitInfo] = useState(null);

  const loadKitInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dealer/kits/remaining/${dealerInfo.dealer_id}`);
      if (res.ok) setKitInfo(await res.json());
    } catch { /* ignore */ }
  }, [dealerInfo.dealer_id]);

  useEffect(() => { loadKitInfo(); }, [loadKitInfo]);

  if (view === 'register') {
    return <RegisterWarrantyScreen dealerInfo={dealerInfo} prefilledWarranty={prefilledWarranty} onBack={() => { setView('home'); setPrefilledWarranty(''); loadKitInfo(); }} onSuccess={() => { setView('home'); setPrefilledWarranty(''); loadKitInfo(); }} />;
  }
  if (view === 'warranties') {
    return <MyWarrantiesScreen dealerInfo={dealerInfo} onBack={() => setView('home')} />;
  }
  if (view === 'my-kits') {
    return <MyKitsScreen dealerInfo={dealerInfo} onBack={() => setView('home')} onRegister={goRegister} />;
  }
  if (view === 'add-lead') {
    return <AddLeadScreen dealerInfo={dealerInfo} onBack={() => setView('home')} onSuccess={() => setView('home')} />;
  }
  if (view === 'leads') {
    return <MyLeadsScreen dealerInfo={dealerInfo} onBack={() => setView('home')} />;
  }
  if (view === 'upload-project') {
    return <DealerUploadProjectScreen dealerInfo={dealerInfo} onBack={() => setView('home')} onSuccess={() => setView('home')} />;
  }
  if (view === 'my-projects') {
    return <DealerMyProjectsScreen dealerInfo={dealerInfo} onBack={() => setView('home')} />;
  }
  if (view === 'manage-users') {
    return <ManageUsersScreen dealerInfo={dealerInfo} onBack={() => setView('home')} />;
  }
  if (view === 'settings') {
    return <SettingsScreen dealerInfo={dealerInfo} onBack={() => setView('home')} />;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>{dealerInfo?.company_name}</h1>
              <p className="text-emerald-100">{dealerInfo?.contact_person}</p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="hidden sm:flex items-center gap-2 text-emerald-100 hover:text-white transition-colors"
                data-testid="dealer-home-btn"
                title="Til hovedsiden"
              >
                <ArrowLeft size={20} /> Til hovedsiden
              </button>
              <button
                onClick={() => navigate('/')}
                className="sm:hidden flex items-center gap-1 text-emerald-100 hover:text-white transition-colors"
                data-testid="dealer-home-btn-mobile"
                aria-label="Til hovedsiden"
              >
                <ArrowLeft size={20} />
              </button>
              <button onClick={onLogout} className="flex items-center gap-2 text-emerald-100 hover:text-white transition-colors" data-testid="dealer-logout-btn">
                <LogOut size={20} /> <span className="hidden sm:inline">Logg ut</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {dealerInfo?.is_demo_account && (
          <div className="mb-6 bg-gradient-to-r from-amber-100 to-amber-50 border-l-4 border-amber-500 rounded-xl p-4 sm:p-5" data-testid="demo-banner">
            <h2 className="font-semibold text-stone-900 text-base sm:text-lg flex items-center gap-2">
              <Shield className="text-amber-600" size={20} /> Demo-modus
            </h2>
            <p className="text-sm text-stone-700 mt-1">
              Du er logget inn med Gravsteinoppfriskerens demo-konto. Du kan registrere det gratis-kittet du fikk, slik kunden vil oppleve det også. <strong>Garantibeviset er ekte og gjelder i 10 år for kunden.</strong>
            </p>
          </div>
        )}

        {/* Kit Status Banner */}
        {kitInfo && (
          <div className={`mb-6 rounded-xl p-5 flex items-center gap-4 ${
            kitInfo.remaining_kits === 0 ? 'bg-red-50 border border-red-200' :
            kitInfo.remaining_kits <= 2 ? 'bg-amber-50 border border-amber-200' :
            'bg-emerald-50 border border-emerald-200'
          }`} data-testid="kit-status-banner">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
              kitInfo.remaining_kits === 0 ? 'bg-red-100' :
              kitInfo.remaining_kits <= 2 ? 'bg-amber-100' : 'bg-emerald-100'
            }`}>
              {kitInfo.remaining_kits <= 2 ? <AlertTriangle className={kitInfo.remaining_kits === 0 ? 'text-red-600' : 'text-amber-600'} size={24} /> : <Package className="text-emerald-700" size={24} />}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-stone-900">
                {kitInfo.remaining_kits === 0 ? 'Ingen kit igjen!' :
                 kitInfo.remaining_kits === 1 ? 'Kun 1 kit igjen!' :
                 `${kitInfo.remaining_kits} kit igjen`}
              </p>
              <p className="text-sm text-stone-600">
                {kitInfo.used_kits} av {kitInfo.total_kits} brukt
              </p>
            </div>
            {kitInfo.remaining_kits <= 2 && (
              <a href="tel:48147777" className="text-sm font-medium text-amber-700 hover:text-amber-800 whitespace-nowrap">
                Ring 48 14 77 77
              </a>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => setView('register')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="register-warranty-card">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Camera className="text-emerald-800" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Registrer garanti</h2>
            <p className="text-stone-600">Ta bilde og registrer ny garanti etter oppfrisking</p>
          </button>

          <button onClick={() => setView('warranties')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="my-warranties-card" style={dealerInfo?.is_demo_account ? {display:'none'} : undefined}>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <ClipboardList className="text-amber-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Mine registreringer</h2>
            <p className="text-stone-600">Se alle garantier du har registrert</p>
          </button>

          <button onClick={() => setView('my-kits')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="my-kits-card">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Package className="text-emerald-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Mine kit</h2>
            <p className="text-stone-600">Se hvilke garantinumre du har, og hvilke som er ledige</p>
          </button>

          {!dealerInfo?.is_demo_account && (<>
          <button onClick={() => setView('add-lead')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="add-lead-card">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <UserPlus className="text-blue-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Oppfrisking uten beskyttelse</h2>
            <p className="text-stone-600">Registrer kunde som ikke kjøpte DC-Protect</p>
          </button>

          <button onClick={() => setView('leads')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="my-leads-card">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <UsersIcon className="text-purple-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Mine oppfølginger</h2>
            <p className="text-stone-600">Se kunder som kan kontaktes for DC-Protect</p>
          </button>

          <button onClick={() => setView('upload-project')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="upload-project-card">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
              <Upload className="text-teal-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Last opp prosjekt</h2>
            <p className="text-stone-600">Last opp for/etter-bilder av oppfrisking</p>
          </button>

          <button onClick={() => setView('my-projects')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="my-projects-card">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
              <FolderOpen className="text-indigo-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Mine prosjekter</h2>
            <p className="text-stone-600">Se og last ned dine opplastede bilder</p>
          </button>

          {!dealerInfo?.parent_dealer_id && (
            <button onClick={() => setView('manage-users')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="manage-users-card">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-4">
                <UsersIcon className="text-stone-700" size={32} />
              </div>
              <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Mine brukere</h2>
              <p className="text-stone-600">Opprett og administrer ansatte som kan logge inn</p>
            </button>
          )}

          <button onClick={() => setView('settings')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="settings-card">
            <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-4">
              <Settings className="text-stone-700" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Innstillinger</h2>
            <p className="text-stone-600">Endre passord og PIN-kode</p>
          </button>
          </>)}
          <button onClick={() => setView('datasheets')} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 text-left" data-testid="datasheets-card">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <FolderOpen className="text-red-600" size={32} />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Datablader</h2>
            <p className="text-stone-600">Last ned produktdatablader</p>
          </button>
        </div>

        <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <h3 className="font-semibold text-stone-900 mb-2 flex items-center gap-2">
            <Shield className="text-emerald-800" size={20} /> Viktig informasjon
          </h3>
          <ul className="text-sm text-stone-700 space-y-1 ml-6 list-disc">
            <li>Garantibevis sendes automatisk til kunde og deg på e-post</li>
            <li>Bruk garantinumrene fra ditt kit</li>
            <li>Ta tydelige bilder av gravsteinen</li>
            <li>10 års garanti gjelder fra registreringsdato</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// My Warranties Screen
function MyWarrantiesScreen({ dealerInfo, onBack }) {
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/dealer/warranties/${dealerInfo.dealer_id}`);
        if (res.ok) setWarranties(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [dealerInfo.dealer_id]);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-warranties-btn">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Mine registreringer</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-stone-500" data-testid="warranties-loading">Laster...</div>
        ) : warranties.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center" data-testid="no-warranties-message">
            <Shield className="mx-auto text-stone-300 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Ingen registreringer ennå</h2>
            <p className="text-stone-500">Garantiene du registrerer vil vises her.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="warranties-list">
            <p className="text-sm text-stone-500 mb-2">{warranties.length} registrering{warranties.length !== 1 ? 'er' : ''}</p>
            {warranties.map((w) => (
              <div key={w.id} className="bg-white rounded-xl shadow-sm border border-stone-200 p-5 hover:shadow-md transition-shadow" data-testid={`warranty-item-${w.warranty_number}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-1 rounded-full">
                        <Shield size={12} /> {w.warranty_number}
                      </span>
                    </div>
                    <h3 className="font-semibold text-stone-900 text-lg">{w.customer_name}</h3>
                    <p className="text-sm text-stone-500 mt-1">{w.address}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-stone-600">
                      <span>Tlf: {w.phone}</span>
                      <span>{w.email}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-stone-400">Registrert</p>
                    <p className="text-sm font-medium text-stone-700">
                      {new Date(w.registration_date).toLocaleDateString('no-NO')}
                    </p>
                    <p className="text-xs text-stone-400 mt-2">Garanti til</p>
                    <p className="text-sm font-medium text-emerald-700">
                      {new Date(w.warranty_end_date).toLocaleDateString('no-NO')}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Register Warranty Screen
function RegisterWarrantyScreen({ dealerInfo, onBack, onSuccess, prefilledWarranty }) {
  const [formData, setFormData] = useState({
    warranty_number: prefilledWarranty || '',
    customer_name: '',
    phone: '',
    address: '',
    email: '',
    gravestone_image: '',
    installer_company: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanFlash, setScanFlash] = useState('');

  const handleScanned = useCallback((text) => {
    const wn = extractWarrantyNumber(text);
    setFormData(prev => ({ ...prev, warranty_number: wn }));
    setShowScanner(false);
    setScanFlash(`Garantinummer fylt inn: ${wn}`);
    setTimeout(() => setScanFlash(''), 2500);
  }, []);

  const compressImage = (file) => {
    // Iteratively compress until under target size, so even 50MP phone photos pass through proxies.
    return new Promise((resolve) => {
      const TARGET_BYTES = 800_000; // ~800 KB base64 string (~600 KB binary)
      const MAX_ITER = 6;
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        let maxWidth = 1600;
        let quality = 0.7;
        let attempt = 0;
        const tryCompress = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          if (dataUrl.length <= TARGET_BYTES || attempt >= MAX_ITER) {
            URL.revokeObjectURL(objectUrl);
            resolve(dataUrl);
            return;
          }
          // Reduce further
          attempt += 1;
          if (quality > 0.4) quality -= 0.1;
          else maxWidth = Math.max(800, Math.floor(maxWidth * 0.85));
          setTimeout(tryCompress, 0);
        };
        tryCompress();
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve('');
      };
      img.src = objectUrl;
    });
  };

  const [imageProcessing, setImageProcessing] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setImageProcessing(true);
    try {
      const compressed = await compressImage(file);
      setFormData(prev => ({...prev, gravestone_image: compressed}));
    } catch {
      setMessage('Kunne ikke behandle bildet. Prøv et annet bilde.');
    }
    setImageProcessing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    try {
      const response = await fetch(`${API}/dealer/warranty/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, dealer_id: dealerInfo.dealer_id })
      });
      // Try to parse JSON regardless of status — gives us the real error from the server.
      let payload = null;
      try { payload = await response.json(); } catch { /* non-JSON response */ }

      if (response.ok) {
        setIsSuccess(true);
        setMessage('Garantien er registrert! Garantibevis er sendt på e-post til både deg og kunden.');
      } else if (payload && payload.detail) {
        setMessage(payload.detail);
      } else {
        setMessage(`Serveren svarte med feil ${response.status}. Prøv igjen, eller ta kontakt med support hvis problemet vedvarer.`);
      }
    } catch (err) {
      // Show the actual error type/message, not just a generic line.
      const detail = err && err.message ? err.message : String(err);
      console.error('Warranty register fetch error:', err);
      setMessage(`Kunne ikke nå serveren (${detail}). Sjekk nettforbindelsen og prøv igjen.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {showScanner && (
        <QRScanner onScan={handleScanned} onClose={() => setShowScanner(false)} />
      )}
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={isSuccess ? onSuccess : onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-to-dashboard-btn">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Registrer ny garanti</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {!isSuccess ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-6" data-testid="dealer-warranty-form">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Garantinummer *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  required
                  value={formData.warranty_number}
                  onChange={(e) => setFormData({...formData, warranty_number: e.target.value.toUpperCase().trim()})}
                  className="flex-1 px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg font-semibold uppercase"
                  placeholder="GDC-2026-0001"
                  data-testid="dealer-warranty-number-input"
                />
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="flex items-center gap-2 px-4 py-3 bg-emerald-800 text-white rounded-lg hover:bg-emerald-900 font-medium whitespace-nowrap"
                  data-testid="dealer-scan-qr-btn"
                >
                  <QrCode size={20} /> Skann QR
                </button>
              </div>
              <p className="text-xs text-stone-500 mt-1">
                Skann QR-koden på kit-etiketten, eller skriv inn nummeret manuelt. Du kan skrive bare sifrene (f.eks. <strong>0145</strong>) eller hele nummeret (<strong>GDC-2025-0145</strong>).
              </p>
              {scanFlash && (
                <p className="text-xs text-emerald-700 mt-1 font-medium" data-testid="dealer-scan-flash">{scanFlash}</p>
              )}
            </div>
            {dealerInfo?.is_demo_account && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-stone-700 mb-2">Utfører — ditt firmanavn *</label>
                <input
                  type="text"
                  required
                  value={formData.installer_company}
                  onChange={(e) => setFormData({...formData, installer_company: e.target.value})}
                  placeholder="Eks: Gravstein Sør AS"
                  className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg"
                  data-testid="dealer-installer-company-input"
                />
                <p className="text-xs text-stone-600 mt-1">Dette firmanavnet vises på kundens garantibevis som ansvarlig utfører.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Kundens navn *</label>
              <input type="text" required value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="dealer-customer-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Telefon *</label>
              <input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="dealer-phone-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Adresse (valgfritt)</label>
              <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="dealer-address-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">E-post *</label>
              <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="dealer-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Bilde av gravsteinen *</label>
              <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
                <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="gravestone-image" required={!selectedFile} data-testid="dealer-image-input" />
                <label htmlFor="gravestone-image" className="cursor-pointer flex flex-col items-center gap-2">
                  <Camera className="text-stone-400" size={48} />
                  <p className="text-sm text-stone-600">{selectedFile ? selectedFile.name : 'Ta bilde eller velg fra galleri'}</p>
                </label>
              </div>
              {selectedFile && (
                <div className="mt-4 bg-emerald-50 p-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ImageIcon className="text-emerald-800" size={24} />
                    <span className="text-sm text-stone-900">{selectedFile.name}</span>
                  </div>
                  <button type="button" onClick={() => { setSelectedFile(null); setFormData(prev => ({...prev, gravestone_image: ''})); }} className="text-red-600 hover:text-red-800"><X size={20} /></button>
                </div>
              )}
            </div>
            {message && !isSuccess && <p className="text-red-600 text-center" data-testid="dealer-error-message">{message}</p>}
            <button type="submit" disabled={isSubmitting || imageProcessing} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-4 text-lg font-medium transition-colors disabled:opacity-50" data-testid="dealer-submit-warranty-btn">
              {imageProcessing ? 'Komprimerer bilde...' : isSubmitting ? 'Registrerer...' : 'Registrer garanti'}
            </button>
          </form>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="dealer-success-message">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-emerald-800" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Garanti registrert!</h2>
            <p className="text-lg text-stone-600 mb-6">{message}</p>
            {dealerInfo?.is_demo_account && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6 text-left max-w-xl mx-auto">
                <h3 className="font-semibold text-stone-900 mb-2 flex items-center gap-2"><Shield className="text-amber-600" size={18} /> Vil du bli ekte forhandler?</h3>
                <p className="text-sm text-stone-700">
                  Kunden din har nå akkurat samme garantibevis som om du allerede var registrert forhandler. Bli partner, så får du:
                </p>
                <ul className="text-sm text-stone-700 mt-2 space-y-1 list-disc list-inside">
                  <li>Eget firmanavn synlig i forhandlerkartet vårt</li>
                  <li>Tilgang til DC-Protect kit til innkjøpspris</li>
                  <li>Egen profil med kundedata og oppfølginger</li>
                </ul>
                <a
                  href="mailto:robert@gravfix.no?subject=Jeg vil bli forhandler"
                  className="mt-3 inline-block bg-amber-500 text-stone-900 hover:bg-amber-400 rounded-lg px-4 py-2 text-sm font-semibold"
                  data-testid="demo-become-dealer-btn"
                >
                  Ta kontakt for å bli forhandler
                </a>
              </div>
            )}
            <button onClick={onSuccess} className="bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-8 py-4 text-lg font-medium transition-colors">
              Tilbake til oversikten
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Add Lead Screen - Register customer without DC-Protect
// =====================  My Kits Screen — overview of warranty numbers per box  =====================
function MyKitsScreen({ dealerInfo, onBack, onRegister }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expandedBox, setExpandedBox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/dealer/kits/overview/${dealerInfo.dealer_id}`);
      if (res.ok) setData(await res.json());
      else {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || 'Kunne ikke hente kit-oversikt');
      }
    } catch (e) {
      setError('Kunne ikke nå serveren');
    }
    setLoading(false);
  }, [dealerInfo.dealer_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (data && data.boxes && data.boxes.length > 0 && expandedBox === null) {
      setExpandedBox(data.boxes[0].box_id);
    }
  }, [data, expandedBox]);

  const matchesSearch = (it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.warranty_number || '').toLowerCase().includes(q)
      || (it.customer_name || '').toLowerCase().includes(q);
  };
  const matchesFilter = (it) => filter === 'all' || (filter === 'free' && !it.is_registered) || (filter === 'used' && it.is_registered);

  const copyNumber = async (wn) => {
    try { await navigator.clipboard.writeText(wn); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="mykits-back-btn">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Mine kit</h1>
              <p className="text-emerald-100 text-sm mt-1">Oversikt over garantinumre i alle dine kit-esker</p>
            </div>
            {data && (
              <div className="flex gap-3 text-sm" data-testid="mykits-summary">
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <div className="text-emerald-100 text-xs uppercase">Totalt</div>
                  <div className="text-lg font-bold">{data.total_kits}</div>
                </div>
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <div className="text-emerald-100 text-xs uppercase">Ledig</div>
                  <div className="text-lg font-bold text-amber-300">{data.remaining_kits}</div>
                </div>
                <div className="bg-white/10 rounded-lg px-3 py-2">
                  <div className="text-emerald-100 text-xs uppercase">Brukt</div>
                  <div className="text-lg font-bold">{data.used_kits}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow border border-stone-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Søk på garantinummer eller kunde..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[180px] px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-sm"
            data-testid="mykits-search-input"
          />
          <div className="flex gap-2">
            {[
              { id: 'all', label: 'Alle' },
              { id: 'free', label: 'Ledige' },
              { id: 'used', label: 'Registrerte' },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 py-2 rounded-lg text-sm whitespace-nowrap ${filter === f.id ? 'bg-emerald-800 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`} data-testid={`mykits-filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="text-stone-500 text-center py-8">Laster...</p>}
        {error && <p className="text-red-600 text-center py-4">{error}</p>}

        {!loading && data && data.boxes.length === 0 && (
          <div className="bg-white rounded-xl shadow border border-stone-200 p-8 text-center">
            <Package className="mx-auto text-stone-400 mb-3" size={48} />
            <p className="text-stone-600">Du har ingen tilordnede kit-esker ennå.</p>
          </div>
        )}

        <div className="space-y-3">
          {!loading && data && data.boxes.map(box => {
            const items = box.items.filter(matchesSearch).filter(matchesFilter);
            if ((search || filter !== 'all') && items.length === 0) return null;
            const isOpen = expandedBox === box.box_id;
            return (
              <div key={box.box_id} className="bg-white rounded-xl shadow border border-stone-200 overflow-hidden" data-testid={`mykits-box-${box.box_number}`}>
                <button
                  onClick={() => setExpandedBox(isOpen ? null : box.box_id)}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-4 hover:bg-stone-50 transition-colors text-left"
                  data-testid={`mykits-toggle-${box.box_number}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="text-emerald-700" size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-stone-900 truncate">{box.box_number}</div>
                      <div className="text-xs text-stone-500">
                        {box.items.length} kit · <span className="text-amber-700 font-medium">{box.free_count} ledig</span> · {box.used_count} brukt
                      </div>
                    </div>
                  </div>
                  <ChevronRight className={`text-stone-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} size={20} />
                </button>

                {isOpen && (
                  <div className="border-t border-stone-100">
                    {items.length === 0 ? (
                      <p className="text-sm text-stone-500 text-center py-4">Ingen treff med valgte filter.</p>
                    ) : (
                      <div className="divide-y divide-stone-100">
                        {items.map(it => (
                          <div key={it.warranty_number} className="px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`mykits-item-${it.warranty_number}`}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {it.is_registered ? (
                                <CheckCircle className="text-emerald-600 flex-shrink-0" size={18} />
                              ) : (
                                <div className="w-[18px] h-[18px] rounded-full border-2 border-amber-400 flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="font-mono text-sm font-semibold text-stone-900">{it.warranty_number}</div>
                                {it.is_registered ? (
                                  <div className="text-xs text-stone-500 truncate">
                                    Registrert til {it.customer_name}
                                    {it.registration_date ? ` · ${new Date(it.registration_date).toLocaleDateString('nb-NO')}` : ''}
                                  </div>
                                ) : (
                                  <div className="text-xs text-amber-700">Ledig — kan brukes</div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => copyNumber(it.warranty_number)} className="text-xs text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-100" data-testid={`mykits-copy-${it.warranty_number}`}>Kopier</button>
                              {!it.is_registered && (
                                <button onClick={() => onRegister && onRegister(it.warranty_number)} className="text-xs bg-emerald-800 text-white hover:bg-emerald-900 px-3 py-1 rounded" data-testid={`mykits-register-${it.warranty_number}`}>Registrer</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function AddLeadScreen({ dealerInfo, onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    customer_name: '', phone: '', email: '', address: '', service_date: new Date().toISOString().slice(0, 10), notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/dealer/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, dealer_id: dealerInfo.dealer_id })
      });
      if (res.ok) {
        setIsSuccess(true);
        setMessage('Kunden er registrert! Du vil bli varslet om 1 år når det er tid for oppfolging.');
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke registrere kunden. Prøv igjen.');
      }
    } catch { setMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={isSuccess ? onSuccess : onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-add-lead">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Registrer oppfrisking uten beskyttelse</h1>
          <p className="text-emerald-100 text-sm mt-1">Kunder som ikke kjopte DC-Protect - automatisk oppfolging om 1 år</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {!isSuccess ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-5" data-testid="add-lead-form">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Kundens navn *</label>
              <input type="text" required value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="lead-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Telefon *</label>
              <input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="lead-phone-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">E-post</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="lead-email-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Adresse / Kirkegård *</label>
              <input type="text" required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="lead-address-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Dato for oppfrisking *</label>
              <input type="date" required value={formData.service_date} onChange={(e) => setFormData({...formData, service_date: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="lead-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Notat</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" rows={3} placeholder="f.eks. 'Ville tenke på det', 'Mente det var for dyrt'" data-testid="lead-notes-input" />
            </div>
            {message && !isSuccess && <p className="text-red-600 text-center" data-testid="lead-error">{message}</p>}
            <button type="submit" disabled={isSubmitting} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-6 py-4 text-lg font-medium transition-colors disabled:opacity-50" data-testid="submit-lead-btn">
              {isSubmitting ? 'Registrerer...' : 'Registrer kunde'}
            </button>
          </form>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="lead-success">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-blue-700" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Kunde registrert!</h2>
            <p className="text-lg text-stone-600 mb-8">{message}</p>
            <button onClick={onSuccess} className="bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-8 py-4 text-lg font-medium transition-colors">
              Tilbake til oversikten
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// My Leads Screen - View and manage follow-up leads
function MyLeadsScreen({ dealerInfo, onBack }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dealer/leads/${dealerInfo.dealer_id}`);
      if (res.ok) setLeads(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [dealerInfo.dealer_id]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await fetch(`${API}/dealer/leads/${leadId}/status?status=${newStatus}`, { method: 'PUT' });
      loadLeads();
    } catch { /* ignore */ }
  };

  const statusLabels = { pending: 'Venter', contacted: 'Kontaktet', converted: 'Kjopt DC-Protect', declined: 'Avslått' };
  const statusColors = { pending: 'bg-stone-100 text-stone-700', contacted: 'bg-amber-100 text-amber-800', converted: 'bg-emerald-100 text-emerald-800', declined: 'bg-red-100 text-red-700' };

  const now = new Date();
  const readyLeads = leads.filter(l => l.status === 'pending' && new Date(l.follow_up_date) <= now);
  const waitingLeads = leads.filter(l => l.status === 'pending' && new Date(l.follow_up_date) > now);
  const handledLeads = leads.filter(l => l.status !== 'pending');

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-leads">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Mine oppfolginger</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-stone-500">Laster...</div>
        ) : leads.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center" data-testid="no-leads">
            <UsersIcon className="mx-auto text-stone-300 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Ingen oppfolginger ennå</h2>
            <p className="text-stone-500">Registrer kunder som ikke kjopte DC-Protect for automatisk oppfolging.</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="leads-list">
            {readyLeads.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-stone-900 mb-3 flex items-center gap-2">
                  <Phone className="text-emerald-700" size={20} /> Klar for oppfolging ({readyLeads.length})
                </h3>
                <div className="space-y-3">
                  {readyLeads.map(l => (
                    <LeadCard key={l.id} lead={l} statusLabels={statusLabels} statusColors={statusColors} onUpdateStatus={updateStatus} highlight />
                  ))}
                </div>
              </div>
            )}
            {waitingLeads.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-stone-900 mb-3">Venter på oppfolging ({waitingLeads.length})</h3>
                <div className="space-y-3">
                  {waitingLeads.map(l => (
                    <LeadCard key={l.id} lead={l} statusLabels={statusLabels} statusColors={statusColors} onUpdateStatus={updateStatus} />
                  ))}
                </div>
              </div>
            )}
            {handledLeads.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-stone-900 mb-3">Håndtert ({handledLeads.length})</h3>
                <div className="space-y-3">
                  {handledLeads.map(l => (
                    <LeadCard key={l.id} lead={l} statusLabels={statusLabels} statusColors={statusColors} onUpdateStatus={updateStatus} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadCard({ lead, statusLabels, statusColors, onUpdateStatus, highlight }) {
  const daysUntil = Math.ceil((new Date(lead.follow_up_date) - new Date()) / (1000 * 60 * 60 * 24));
  return (
    <div className={`bg-white rounded-xl shadow-sm border p-5 ${highlight ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-stone-200'}`} data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[lead.status]}`}>
              {statusLabels[lead.status]}
            </span>
            {lead.status === 'pending' && (
              <span className="text-xs text-stone-400">
                {daysUntil <= 0 ? 'Klar for oppfolging!' : `Oppfolging om ${daysUntil} dager`}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-stone-900 text-lg">{lead.customer_name}</h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-stone-600">
            <span>Tlf: {lead.phone}</span>
            {lead.email && <span>{lead.email}</span>}
          </div>
          <p className="text-sm text-stone-500 mt-1">{lead.address}</p>
          {lead.notes && <p className="text-sm text-stone-400 italic mt-1">"{lead.notes}"</p>}
          <p className="text-xs text-stone-400 mt-2">Oppfrisket: {lead.service_date?.slice(0, 10)}</p>
        </div>
        {lead.status === 'pending' && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button onClick={() => onUpdateStatus(lead.id, 'contacted')} className="text-xs bg-amber-100 text-amber-800 hover:bg-amber-200 px-3 py-1.5 rounded-lg font-medium transition-colors" data-testid={`lead-contacted-${lead.id}`}>Kontaktet</button>
            <button onClick={() => onUpdateStatus(lead.id, 'converted')} className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-3 py-1.5 rounded-lg font-medium transition-colors" data-testid={`lead-converted-${lead.id}`}>Kjopt!</button>
            <button onClick={() => onUpdateStatus(lead.id, 'declined')} className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-lg font-medium transition-colors" data-testid={`lead-declined-${lead.id}`}>Avslått</button>
          </div>
        )}
        {lead.status === 'contacted' && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <button onClick={() => onUpdateStatus(lead.id, 'converted')} className="text-xs bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-3 py-1.5 rounded-lg font-medium transition-colors">Kjopt!</button>
            <button onClick={() => onUpdateStatus(lead.id, 'declined')} className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded-lg font-medium transition-colors">Avslått</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Dealer Upload Project Screen
function DealerUploadProjectScreen({ dealerInfo, onBack, onSuccess }) {
  const [formData, setFormData] = useState({
    gravestone_name: '', cemetery: '', municipality: '', work_date: new Date().toISOString().slice(0, 10),
    notes: '', consent_website: false, consent_social_media: false, published: false
  });
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const compressDealerImage = (file, maxWidth = 1600, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFiles = (files, type) => {
    const fileArray = Array.from(files).slice(0, 4);
    const promises = fileArray.map(file => compressDealerImage(file));
    Promise.all(promises).then(results => {
      if (type === 'before') setBeforeImages(prev => [...prev, ...results].slice(0, 4));
      else setAfterImages(prev => [...prev, ...results].slice(0, 4));
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (beforeImages.length === 0 || afterImages.length === 0) {
      setMessage('Du må laste opp minst 1 for-bilde og 1 etter-bilde');
      return;
    }
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/dealer/projects?dealer_id=${dealerInfo.dealer_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, before_images: beforeImages, after_images: afterImages })
      });
      if (res.ok) {
        setIsSuccess(true);
        setMessage('Prosjektet er lastet opp! Bildene er nå tilgjengelige under "Mine prosjekter".');
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke laste opp prosjektet. Prøv igjen.');
      }
    } catch { setMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={isSuccess ? onSuccess : onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-upload-project">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Last opp for/etter-bilder</h1>
          <p className="text-emerald-100 text-sm mt-1">Ta bilder av gravsteinen for og etter oppfrisking</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {!isSuccess ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6 space-y-5" data-testid="dealer-upload-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Gravsteinens navn *</label>
                <input type="text" required value={formData.gravestone_name} onChange={(e) => setFormData({...formData, gravestone_name: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" placeholder="f.eks. Hansen" data-testid="dealer-project-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Kirkegård *</label>
                <input type="text" required value={formData.cemetery} onChange={(e) => setFormData({...formData, cemetery: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" placeholder="f.eks. Vestre Gravlund" data-testid="dealer-project-cemetery" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Kommune *</label>
                <input type="text" required value={formData.municipality} onChange={(e) => setFormData({...formData, municipality: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" placeholder="f.eks. Oslo" data-testid="dealer-project-municipality" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Dato *</label>
                <input type="date" required value={formData.work_date} onChange={(e) => setFormData({...formData, work_date: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-lg" data-testid="dealer-project-date" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Notat</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" rows={2} placeholder="Beskrivelse av arbeidet..." />
            </div>

            {/* Before Images */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">For-bilder (maks 4) *</label>
              <div className="flex gap-3 flex-wrap">
                {beforeImages.map((img, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-stone-300">
                    <img src={img} alt={`For ${i+1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setBeforeImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                    <span className="absolute bottom-0.5 left-0.5 bg-stone-900/70 text-white text-xs px-1 rounded">FOR</span>
                  </div>
                ))}
                {beforeImages.length < 4 && (
                  <label className="w-24 h-24 border-2 border-dashed border-stone-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-stone-400 text-stone-400">
                    <Camera size={24} /><span className="text-xs mt-1">Ta bilde</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files, 'before')} data-testid="dealer-before-images" />
                  </label>
                )}
              </div>
            </div>

            {/* After Images */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Etter-bilder (maks 4) *</label>
              <div className="flex gap-3 flex-wrap">
                {afterImages.map((img, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-emerald-300">
                    <img src={img} alt={`Etter ${i+1}`} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setAfterImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">x</button>
                    <span className="absolute bottom-0.5 left-0.5 bg-emerald-800/70 text-white text-xs px-1 rounded">ETTER</span>
                  </div>
                ))}
                {afterImages.length < 4 && (
                  <label className="w-24 h-24 border-2 border-dashed border-emerald-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-400 text-emerald-400">
                    <Camera size={24} /><span className="text-xs mt-1">Ta bilde</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files, 'after')} data-testid="dealer-after-images" />
                  </label>
                )}
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={formData.consent_website} onChange={(e) => setFormData({...formData, consent_website: e.target.checked})} className="w-5 h-5 rounded border-stone-300 text-emerald-800 focus:ring-emerald-800" />
              <span className="text-sm text-stone-700">Samtykke til visning på nettside</span>
            </label>

            {message && !isSuccess && <p className="text-red-600 text-center">{message}</p>}
            <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-4 text-lg font-medium transition-colors disabled:opacity-50" data-testid="dealer-submit-project-btn">
              {isSubmitting ? 'Laster opp bilder...' : 'Last opp prosjekt'}
            </button>
          </form>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center" data-testid="dealer-project-success">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-teal-700" size={48} />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Prosjekt lastet opp!</h2>
            <p className="text-lg text-stone-600 mb-8">{message}</p>
            <button onClick={onSuccess} className="bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-8 py-4 text-lg font-medium transition-colors">Tilbake til oversikten</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Dealer My Projects Screen
function DealerMyProjectsScreen({ dealerInfo, onBack }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/dealer/projects/${dealerInfo.dealer_id}`);
        if (res.ok) setProjects(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [dealerInfo.dealer_id]);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-projects">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Mine prosjekter</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-12 text-stone-500">Laster...</div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center" data-testid="no-projects">
            <FolderOpen className="mx-auto text-stone-300 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-stone-700 mb-2">Ingen prosjekter ennå</h2>
            <p className="text-stone-500">Last opp for/etter-bilder for å se dem her.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="dealer-projects-list">
            <p className="text-sm text-stone-500">{projects.length} prosjekt{projects.length !== 1 ? 'er' : ''}</p>
            {projects.map((p) => {
              const beforeImgs = (p.images || []).filter(i => i.category === 'before').sort((a, b) => a.position - b.position);
              const afterImgs = (p.images || []).filter(i => i.category === 'after').sort((a, b) => a.position - b.position);
              return (
                <div key={p.id} className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden" data-testid={`dealer-project-${p.id}`}>
                  {/* Image grid */}
                  <div className="p-4">
                    <h3 className="text-xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>{p.gravestone_name}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-500 mb-4">
                      <span className="flex items-center gap-1"><MapPin size={14} /> {p.cemetery}, {p.municipality}</span>
                      <span className="flex items-center gap-1"><Calendar size={14} /> {p.work_date}</span>
                    </div>
                    {p.notes && <p className="text-sm text-stone-400 italic mb-4">"{p.notes}"</p>}

                    {/* Before images */}
                    {beforeImgs.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-stone-500 mb-2 uppercase">For-bilder</p>
                        <div className="flex gap-2 overflow-x-auto">
                          {beforeImgs.map((img, i) => (
                            <div key={i} className="relative flex-shrink-0">
                              <img src={img.image_url} alt={`For ${i+1}`} className="w-32 h-32 object-cover rounded-lg border-2 border-stone-200" />
                              <a href={img.image_url} download target="_blank" rel="noreferrer" className="absolute bottom-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white shadow-sm" title="Last ned">
                                <Download size={14} className="text-stone-700" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* After images */}
                    {afterImgs.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-emerald-700 mb-2 uppercase">Etter-bilder</p>
                        <div className="flex gap-2 overflow-x-auto">
                          {afterImgs.map((img, i) => (
                            <div key={i} className="relative flex-shrink-0">
                              <img src={img.image_url} alt={`Etter ${i+1}`} className="w-32 h-32 object-cover rounded-lg border-2 border-emerald-200" />
                              <a href={img.image_url} download target="_blank" rel="noreferrer" className="absolute bottom-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white shadow-sm" title="Last ned">
                                <Download size={14} className="text-emerald-700" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-3 text-xs text-stone-400">
                      {p.published && <span className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-medium">Publisert på nettside</span>}
                      {p.consent_website && <span>Samtykke: nettside</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DealerPortal;

// Manage Users Screen
function ManageUsersScreen({ dealerInfo, onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', contact_person: '', pin_code: '' });
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dealer/users/${dealerInfo.dealer_id}`);
      if (res.ok) setUsers(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  }, [dealerInfo.dealer_id]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newUser.pin_code.length !== 4) { setMessage('PIN-kode må være 4 siffer'); return; }
    setIsSubmitting(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/dealer/users/${dealerInfo.dealer_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      if (res.ok) {
        setMessage('Bruker opprettet!');
        setNewUser({ username: '', password: '', contact_person: '', pin_code: '' });
        setShowCreate(false);
        loadUsers();
      } else {
        const err = await res.json();
        setMessage(err.detail || 'Kunne ikke opprette bruker. Sjekk at brukernavn er ledig.');
      }
    } catch { setMessage('Kunne ikke koble til serveren. Sjekk internettforbindelsen.'); }
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Er du sikker på at du vil slette bruker "${name}"?`)) return;
    try {
      const res = await fetch(`${API}/dealer/users/${dealerInfo.dealer_id}/${userId}`, { method: 'DELETE' });
      if (res.ok) loadUsers();
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-4">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-2" data-testid="back-from-users">
            <ArrowLeft size={16} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Mine brukere</h1>
          <p className="text-emerald-200 text-sm mt-1">Opprett ansatte som kan logge inn og registrere garantier</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {message && (
          <div className={`mb-4 p-4 rounded-xl text-sm font-medium ${message.includes('opprettet') ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-600'}`}>
            {message}
          </div>
        )}

        <button onClick={() => setShowCreate(!showCreate)} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-xl px-6 py-4 font-medium transition-colors mb-6 flex items-center justify-center gap-2" data-testid="create-user-btn">
          <UserPlus size={20} /> {showCreate ? 'Avbryt' : 'Opprett ny bruker'}
        </button>

        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border border-stone-200 p-5 mb-6 space-y-4" data-testid="create-user-form">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Navn på ansatt *</label>
              <input type="text" required value={newUser.contact_person} onChange={(e) => setNewUser({...newUser, contact_person: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="F.eks. Ola Nordmann" data-testid="new-user-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Brukernavn *</label>
              <input type="text" required value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="F.eks. ola_ansatt" data-testid="new-user-username" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Passord *</label>
              <input type="password" required value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent" placeholder="Velg et sterkt passord" data-testid="new-user-password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">PIN-kode (4 siffer) *</label>
              <input type="text" required maxLength={4} pattern="[0-9]{4}" value={newUser.pin_code} onChange={(e) => setNewUser({...newUser, pin_code: e.target.value.replace(/\D/g, '').slice(0, 4)})} className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-800 focus:border-transparent text-center text-2xl tracking-widest" placeholder="____" data-testid="new-user-pin" />
            </div>
            <button type="submit" disabled={isSubmitting} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-6 py-3 font-medium transition-colors disabled:opacity-50" data-testid="submit-new-user">
              {isSubmitting ? 'Oppretter...' : 'Opprett bruker'}
            </button>
          </form>
        )}

        {loading ? (
          <div className="text-stone-500 text-center py-8">Laster...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
            <UsersIcon className="mx-auto text-stone-300 mb-3" size={48} />
            <p className="text-stone-500">Ingen brukere opprettet ennå</p>
            <p className="text-stone-400 text-sm mt-1">Trykk "Opprett ny bruker" for å legge til ansatte</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="users-list">
            {users.map((user) => (
              <div key={user.id} className="bg-white rounded-xl border border-stone-200 p-5 flex items-center justify-between" data-testid={`user-${user.id}`}>
                <div>
                  <h3 className="font-semibold text-stone-900">{user.contact_person}</h3>
                  <p className="text-sm text-stone-500">Brukernavn: {user.username}</p>
                  {user.created_date && <p className="text-xs text-stone-400 mt-1">Opprettet: {new Date(user.created_date).toLocaleDateString('no-NO')}</p>}
                </div>
                <button onClick={() => handleDelete(user.id, user.contact_person)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors" data-testid={`delete-user-${user.id}`}>
                  <X size={20} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DatasheetsScreen({ onBack }) {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/dealer/datasheets`)
      .then(res => res.ok ? res.json() : [])
      .then(data => setSheets(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-6">
        <div className="max-w-4xl mx-auto px-4">
          <button onClick={onBack} className="flex items-center gap-2 text-emerald-100 hover:text-white mb-4" data-testid="back-from-datasheets">
            <ArrowLeft size={20} /> Tilbake
          </button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Datablader</h1>
          <p className="text-emerald-200 text-sm mt-1">Last ned produktdatablader for DC-Protect</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <p className="text-stone-400 text-center py-12">Laster datablader...</p>
        ) : sheets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
            <FolderOpen className="mx-auto text-stone-300 mb-4" size={48} />
            <p className="text-stone-500">Ingen datablader tilgjengelige ennå.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sheets.map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-stone-200 p-6 flex items-center gap-4" data-testid={`dealer-sheet-${s.id}`}>
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-red-600">PDF</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-semibold text-stone-900">{s.product_name}</p>
                  <p className="text-sm text-stone-400">{s.filename}</p>
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-emerald-800 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-emerald-900 transition-colors text-sm"
                  data-testid={`download-sheet-${s.id}`}
                >
                  <Download size={16} />
                  Last ned
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



function SettingsScreen({ dealerInfo, onBack }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [pinMsg, setPinMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 4) { setPasswordMsg('Nytt passord må være minst 4 tegn'); return; }
    setSaving(true);
    setPasswordMsg('');
    try {
      const res = await fetch(`${API}/dealer/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealer_id: dealerInfo.dealer_id, current_password: currentPassword, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordMsg('Passord endret!');
        setCurrentPassword('');
        setNewPassword('');
      } else {
        setPasswordMsg(data.detail || 'Kunne ikke endre passord. Sjekk at nåværende passord er riktig.');
      }
    } catch { setPasswordMsg('Kunne ikke koble til serveren'); }
    finally { setSaving(false); }
  };

  const changePin = async (e) => {
    e.preventDefault();
    if (newPin.length !== 4 || !/^\d{4}$/.test(newPin)) { setPinMsg('Ny PIN må være 4 siffer'); return; }
    setSaving(true);
    setPinMsg('');
    try {
      const res = await fetch(`${API}/dealer/change-pin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealer_id: dealerInfo.dealer_id, current_pin: currentPin, new_pin: newPin })
      });
      const data = await res.json();
      if (res.ok) {
        setPinMsg('PIN-kode endret!');
        setCurrentPin('');
        setNewPin('');
      } else {
        setPinMsg(data.detail || 'Kunne ikke endre PIN. Sjekk at nåværende PIN er riktig.');
      }
    } catch { setPinMsg('Kunne ikke koble til serveren'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-emerald-900 text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-3">
          <button onClick={onBack} className="text-emerald-100 hover:text-white" data-testid="settings-back-btn"><ArrowLeft size={24} /></button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Innstillinger</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        {/* Change Password */}
        <form onSubmit={changePassword} className="bg-white rounded-xl shadow-sm border border-stone-200 p-6" data-testid="change-password-form">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Endre passord</h2>
          <div className="space-y-3">
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Nåværende passord" required className="w-full px-4 py-3 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="current-password-input" />
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Nytt passord (minst 4 tegn)" required className="w-full px-4 py-3 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="new-password-input" />
            <button type="submit" disabled={saving} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-3 font-medium transition-colors disabled:opacity-50" data-testid="change-password-btn">
              {saving ? 'Endrer...' : 'Endre passord'}
            </button>
          </div>
          {passwordMsg && <p className={`text-sm mt-3 text-center ${passwordMsg.includes('endret') ? 'text-emerald-600' : 'text-red-600'}`}>{passwordMsg}</p>}
        </form>

        {/* Change PIN */}
        <form onSubmit={changePin} className="bg-white rounded-xl shadow-sm border border-stone-200 p-6" data-testid="change-pin-form">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Endre PIN-kode</h2>
          <div className="space-y-3">
            <input type="password" value={currentPin} onChange={(e) => setCurrentPin(e.target.value)} placeholder="Nåværende PIN (4 siffer)" required maxLength={4} className="w-full px-4 py-3 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="current-pin-input" />
            <input type="password" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="Ny PIN (4 siffer)" required maxLength={4} className="w-full px-4 py-3 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-800 focus:border-transparent" data-testid="new-pin-input" />
            <button type="submit" disabled={saving} className="w-full bg-emerald-800 text-white hover:bg-emerald-900 rounded-lg px-4 py-3 font-medium transition-colors disabled:opacity-50" data-testid="change-pin-btn">
              {saving ? 'Endrer...' : 'Endre PIN-kode'}
            </button>
          </div>
          {pinMsg && <p className={`text-sm mt-3 text-center ${pinMsg.includes('endret') ? 'text-emerald-600' : 'text-red-600'}`}>{pinMsg}</p>}
        </form>
      </div>
    </div>
  );
}
